import React, { useEffect, useState } from 'react';
import { informePublicoAPI } from '../../services/api';
import { armarLiquidacion } from '../../utils/liquidacion';
import { generarInformePDF, generarInformeExcel } from '../../utils/liquidacionReportes';
import { nombreMes } from '../../utils/periodos';

// Página del gerente (/informe/<token>): sin usuario. Elige el mes, ajusta la
// TRM (por defecto 3.500, solo para su informe) y descarga el PDF o el Excel.

const Marca = () => (
  <div className="informe-pub-marca">
    <span className="brand-logo">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="2" y="14" width="4" height="7" rx="0.5" fill="currentColor" opacity="0.4" />
        <rect x="8" y="10" width="4" height="11" rx="0.5" fill="currentColor" opacity="0.6" />
        <rect x="14" y="6" width="4" height="15" rx="0.5" fill="currentColor" opacity="0.8" />
        <polyline points="2 12 7 8 13 10 22 3" strokeWidth="2.2" />
        <polyline points="17 3 22 3 22 8" strokeWidth="2.2" />
      </svg>
    </span>
    <span className="brand-name">CostProject</span>
  </div>
);

const IconoDescarga = () => (
  <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
    <path d="M26 24v4H6v-4H4v4a2 2 0 0 0 2 2h20a2 2 0 0 0 2-2v-4Z" />
    <path d="M26 14l-1.41-1.41L17 20.17V2h-2v18.17l-7.59-7.58L6 14l10 10 10-10Z" />
  </svg>
);

export const InformePublico = ({ token }) => {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [periodo, setPeriodo] = useState('');
  const [trm, setTrm] = useState('');
  const [generando, setGenerando] = useState(null); // 'pdf' | 'excel'
  const [aviso, setAviso] = useState(null); // { tipo, texto }

  useEffect(() => {
    informePublicoAPI.get(token)
      .then((res) => {
        setInfo(res.data);
        setPeriodo(res.data.periodos[0] || '');
        setTrm(String(res.data.trm_defecto));
      })
      .catch((e) => setError(e.response?.data?.error || 'No se pudo abrir el enlace. Intenta más tarde.'));
  }, [token]);

  const trmNum = parseFloat(String(trm).replace(/\./g, '').replace(',', '.'));
  const trmValida = trmNum > 0 && trmNum < 100000;

  const descargar = async (formato) => {
    setGenerando(formato);
    setAviso(null);
    const nombre = formato === 'excel' ? 'Excel' : 'PDF';
    try {
      const res = await informePublicoAPI.liquidacion(token, periodo, trmNum, formato);
      const opciones = {
        liq: armarLiquidacion(res.data),
        nombreMes: nombreMes(periodo),
        onSuccess: () => setAviso({ tipo: 'ok', texto: `Se descargó el ${nombre} de ${nombreMes(periodo)}.` }),
        onError: () => setAviso({ tipo: 'error', texto: `No se pudo generar el ${nombre}. Intenta de nuevo.` })
      };
      if (formato === 'excel') await generarInformeExcel({ ...opciones, soloDatos: true });
      else generarInformePDF(opciones);
    } catch (e) {
      setAviso({ tipo: 'error', texto: e.response?.data?.error || 'No se pudo generar el informe. Intenta de nuevo.' });
    } finally {
      setGenerando(null);
    }
  };

  return (
    <div className="informe-pub">
      <header className="informe-pub-header"><Marca /></header>
      <main className="informe-pub-main">
        <section className="informe-pub-card">
          {error ? (
            <>
              <h1>Enlace no disponible</h1>
              <p className="informe-pub-sub">{error}</p>
              <p className="informe-pub-ayuda">Pide un enlace nuevo a quien te lo compartió.</p>
            </>
          ) : !info ? (
            <p className="informe-pub-sub">Cargando…</p>
          ) : (
            <>
              <span className="informe-pub-eyebrow">Informe de costos I+D</span>
              <h1>Descarga el informe del mes</h1>
              <p className="informe-pub-sub">
                Costo Cloud + costo de proveedores, por cliente y por área (Mesa de Servicio y Soporte en Sitio).
              </p>

              {info.periodos.length === 0 ? (
                <p className="informe-pub-ayuda">Todavía no hay meses cargados.</p>
              ) : (
                <>
                  <label className="consumo-campo">
                    <span>Mes</span>
                    <select value={periodo} onChange={(e) => setPeriodo(e.target.value)}>
                      {info.periodos.map((p) => <option key={p} value={p}>{nombreMes(p)}</option>)}
                    </select>
                  </label>
                  <label className="consumo-campo">
                    <span>TRM (pesos por dólar)</span>
                    <input
                      inputMode="decimal"
                      value={trm}
                      onChange={(e) => setTrm(e.target.value)}
                      aria-invalid={!trmValida}
                    />
                    <small>Convierte el consumo de IBM Cloud (USD) a pesos. Solo aplica a este informe.</small>
                  </label>

                  <div className="informe-pub-acciones">
                    <button
                      type="button"
                      className="btn-primary informe-pub-btn"
                      disabled={!periodo || !trmValida || Boolean(generando)}
                      onClick={() => descargar('pdf')}
                    >
                      {generando === 'pdf' ? 'Generando…' : 'Descargar PDF'}
                      <IconoDescarga />
                    </button>
                    <button
                      type="button"
                      className="btn-secondary informe-pub-btn informe-pub-btn-excel"
                      disabled={!periodo || !trmValida || Boolean(generando)}
                      onClick={() => descargar('excel')}
                    >
                      {generando === 'excel' ? 'Generando…' : 'Descargar Excel'}
                      <IconoDescarga />
                    </button>
                  </div>
                  {aviso && <p className={`informe-pub-aviso ${aviso.tipo}`} role="status">{aviso.texto}</p>}
                </>
              )}
            </>
          )}
        </section>
      </main>
    </div>
  );
};
